// components/SucursalesMercadoLibre/EditarSucursalModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Storefront,
  LocationOn,
  Map,
  Info,
  Numbers,
} from '@mui/icons-material';

const EditarSucursalModal = ({ open, sucursal, onClose, onActualizar }) => {
  const [formData, setFormData] = useState({
    name: '',
    street_name: '',
    street_number: '',
    city_name: '',
    state_name: '',
    latitude: '',
    longitude: '',
    reference: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (sucursal) {
      const location = sucursal.location || {};
      setFormData({
        name: sucursal.name || '',
        street_name: location.street_name || '',
        street_number: location.street_number || '',
        city_name: location.city_name || location.city || '',
        state_name: location.state_name || '',
        latitude: location.latitude || '',
        longitude: location.longitude || '',
        reference: location.reference || ''
      });
    }
  }, [sucursal]);

  const validarLatitud = (lat) => {
    if (!lat) return true;
    const num = parseFloat(lat);
    return !isNaN(num) && num >= -90 && num <= 90;
  };

  const validarLongitud = (lng) => {
    if (!lng) return true;
    const num = parseFloat(lng);
    return !isNaN(num) && num >= -180 && num <= 180;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.street_name.trim()) {
      newErrors.street_name = 'La calle es requerida';
    }
    if (!formData.city_name.trim()) {
      newErrors.city_name = 'La ciudad es requerida';
    }
    if (!formData.state_name.trim()) {
      newErrors.state_name = 'La provincia es requerida';
    }
    
    if (formData.latitude && !validarLatitud(formData.latitude)) {
      newErrors.latitude = 'Latitud inválida (-90 a 90)';
    }
    if (formData.longitude && !validarLongitud(formData.longitude)) {
      newErrors.longitude = 'Longitud inválida (-180 a 180)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        location: {
          street_name: formData.street_name.trim(),
          street_number: formData.street_number || '',
          city_name: formData.city_name.trim(),
          state_name: formData.state_name.trim(),
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          reference: formData.reference?.trim() || ''
        }
      };
      
      await onActualizar(sucursal.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error al actualizar:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Storefront sx={{ color: '#009ee3' }} />
          Editar Sucursal - {sucursal?.name}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Nombre de la sucursal *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Storefront /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="ID Externo"
              value={sucursal?.external_id || ''}
              disabled
              helperText="No editable"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Calle *"
              name="street_name"
              value={formData.street_name}
              onChange={handleChange}
              error={!!errors.street_name}
              helperText={errors.street_name}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Número"
              name="street_number"
              value={formData.street_number}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Numbers /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Ciudad *"
              name="city_name"
              value={formData.city_name}
              onChange={handleChange}
              error={!!errors.city_name}
              helperText={errors.city_name}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Provincia *"
              name="state_name"
              value={formData.state_name}
              onChange={handleChange}
              error={!!errors.state_name}
              helperText={errors.state_name}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Latitud"
              name="latitude"
              type="text"
              value={formData.latitude}
              onChange={handleChange}
              error={!!errors.latitude}
              helperText={errors.latitude || "Opcional - Ej: -32.9477"}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Map /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Longitud"
              name="longitude"
              type="text"
              value={formData.longitude}
              onChange={handleChange}
              error={!!errors.longitude}
              helperText={errors.longitude || "Opcional - Ej: -60.6376"}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Map /></InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Referencia"
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Info /></InputAdornment>,
              }}
              helperText="Opcional - Punto de referencia"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={submitting}
          sx={{ bgcolor: '#009ee3', '&:hover': { bgcolor: '#0080b3' } }}
        >
          {submitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarSucursalModal;